/**
 * Ligne de saisie pour ajout bulk de charges fixes
 * @module components/tresorerie/BulkChargesFixesRow
 */

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Copy,
  Trash2,
  Home,
  Shield,
  Car,
  FileText,
  Zap,
  Fuel,
  Landmark,
  Building2,
  FileSpreadsheet,
  Pin,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BulkChargeFixeData } from '@/types/bulk';
import {
  CategorieChargeFix,
  FrequenceChargeFix,
  CATEGORIES_LABELS,
  FREQUENCES_LABELS,
} from '@/types/charges-fixes.types';

interface BulkChargesFixesRowProps {
  index: number;
  data: BulkChargeFixeData;
  onChange: (index: number, data: Partial<BulkChargeFixeData>) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  canDelete: boolean;
  errors: Record<string, string>;
}

// Mapping des icônes par catégorie
const CATEGORY_ICONS: Record<CategorieChargeFix, React.ElementType> = {
  loyer: Home,
  assurance: Shield,
  leasing: Car,
  abonnement: FileText,
  energie: Zap,
  vehicules: Fuel,
  emprunt: Landmark,
  comptable: Building2,
  taxes: FileSpreadsheet,
  autre: Pin,
};

export function BulkChargesFixesRow({
  index,
  data,
  onChange,
  onDuplicate,
  onDelete,
  canDelete,
  errors,
}: BulkChargesFixesRowProps) {
  const Icon = CATEGORY_ICONS[data.categorie];

  return (
    <div className="grid grid-cols-[1fr_160px_120px_80px_120px_80px_auto] gap-3 items-start p-3 bg-card/50 rounded-lg border border-border/50">
      {/* Nom */}
      <div>
        <Input
          placeholder="Nom de la charge *"
          value={data.nom}
          onChange={(e) => onChange(index, { nom: e.target.value })}
          className={errors.nom ? 'border-red-500' : ''}
        />
        {errors.nom && <span className="text-xs text-red-500">{errors.nom}</span>}
      </div>

      {/* Catégorie */}
      <Select
        value={data.categorie}
        onValueChange={(value) => onChange(index, { categorie: value as CategorieChargeFix })}
      >
        <SelectTrigger>
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <SelectValue placeholder="Catégorie" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(CATEGORIES_LABELS) as CategorieChargeFix[]).map((cat) => {
            const CatIcon = CATEGORY_ICONS[cat];
            return (
              <SelectItem key={cat} value={cat}>
                <div className="flex items-center gap-2">
                  <CatIcon className="h-4 w-4" />
                  <span>{CATEGORIES_LABELS[cat]}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {/* Montant HT */}
      <div>
        <div className="relative">
          <Input
            type="number"
            placeholder="Montant"
            value={data.montant_ht || ''}
            onChange={(e) => onChange(index, { montant_ht: parseFloat(e.target.value) || 0 })}
            className={`pr-6 ${errors.montant_ht ? 'border-red-500' : ''}`}
            min={0}
            step={10}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
        </div>
        {errors.montant_ht && <span className="text-xs text-red-500">{errors.montant_ht}</span>}
      </div>

      {/* TVA % */}
      <Select
        value={data.taux_tva.toString()}
        onValueChange={(value) => onChange(index, { taux_tva: parseFloat(value) })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">0%</SelectItem>
          <SelectItem value="5.5">5,5%</SelectItem>
          <SelectItem value="10">10%</SelectItem>
          <SelectItem value="20">20%</SelectItem>
        </SelectContent>
      </Select>

      {/* Fréquence */}
      <Select
        value={data.frequence}
        onValueChange={(value) => onChange(index, { frequence: value as FrequenceChargeFix })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Fréquence" />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(FREQUENCES_LABELS) as FrequenceChargeFix[]).map((freq) => (
            <SelectItem key={freq} value={freq}>
              {FREQUENCES_LABELS[freq]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Jour de prélèvement */}
      <div>
        <Input
          type="number"
          placeholder="Jour"
          value={data.jour_prelevement || ''}
          onChange={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!e.target.value || (val >= 1 && val <= 31)) {
              onChange(index, { jour_prelevement: val || 5 });
            }
          }}
          className={errors.jour_prelevement ? 'border-red-500' : ''}
          min={1}
          max={31}
        />
        <p className="text-xs text-muted-foreground mt-1">Jour du mois où le prélèvement est effectué (ajusté en fin de mois si nécessaire).</p>
        {errors.jour_prelevement && <span className="text-xs text-red-500">{errors.jour_prelevement}</span>}
      </div>

      {/* Actions */}
      <div className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onDuplicate(index)}
          title="Dupliquer cette ligne"
        >
          <Copy className="h-4 w-4" />
        </Button>
        {canDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(index)}
            title="Supprimer cette ligne"
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
